import isEqual from 'lodash/isEqual';
import { useState, useCallback, useEffect } from 'react';
// @mui
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import IconButton from '@mui/material/IconButton';
import TableContainer from '@mui/material/TableContainer';
// routes
import { paths } from 'src/routes/paths';
// _mock
import { _roles, USER_STATUS_OPTIONS } from 'src/_mock';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// components
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import {
    useTable, emptyRows, TableEmptyRows,
    TableHeadCustom,
    TableSelectedAction,
    getComparator
} from 'src/components/table';
import UserTableToolbar from './supportTicket-table-toolebar';
import UserTableFiltersResult from './supportTicket-table-filter';
import UserTableRow from './supportTicket-table-row';
import { SupportTicketCreateModal } from './create-form-fields';
import { deleteRequest, getRequest, postRequest } from 'src/services';
import { endpoints } from 'src/utils/axios';
import TablePaginationApiBased from 'src/components/table/table-pagination-api-based-urls';
import { Box, MenuItem, Select } from '@mui/material';
import { LoadingScreen } from 'src/components/loading-screen';
import { applyFilter } from 'src/services/fetching';
import { useAuthContext } from 'src/auth/hooks';
import { RHFSelect } from 'src/components/hook-form';
//

// ----------------------------------------------------------------------

const STATUS_OPTIONS = [ { value: 'all', label: 'All' }, ...USER_STATUS_OPTIONS ];

const TABLE_HEAD = [
    { id: 'name', label: 'Name' },
    { id: 'description', label: 'Description' },
    { id: 'email', label: 'Email' },
    { id: 'status', label: 'Status' },
    { id: 'file', label: 'File' },
    { id: 'status', label: 'Action' },
];

const defaultFilters = {
    name: '',
    role: [],
    status: 'all',
    active: 'all',
};

// ----------------------------------------------------------------------

export default function SupportTicketTable() {
    const [ tableData, setTableData ] = useState({ data: [], pagination: {} });
    console.log(tableData, 'tableData')
    const [ getLoading, setGetLoading ] = useState(false);
    const [ deleteLoading, setDeleteLoading ] = useState(false);
    const [ selectedItem, setSelectedItem ] = useState({});
    const [ loading, setLoading ] = useState(false);
    const [ adminUsers, setAdminUsers ] = useState()
    const [ selectedProject, setSelectedProject ] = useState('All');


    const table = useTable();
    const settings = useSettingsContext();
    const confirm = useBoolean();
    const create = useBoolean();

    const { user } = useAuthContext();
    const userPermissions = user?.roles?.flatMap(role => role?.permissions?.map(perm => perm?.name)) || [];
    const hasPermission = (keyword) => userPermissions?.some(permission => permission === keyword);
    const canCreate = hasPermission('support_tickets.create');


    const handleChange = (event) => {
        const value = event.target.value;
        setSelectedProject(value); // API call handled in useEffect
    };


    useEffect(() => {
        getSupportTicket(1);
    }, [ selectedProject ]);


    const getSupportTicket = async (page = 1) => {
        setLoading(true);
        let params = `?page=${page}`;

        if (selectedProject === 'Assign to me') {
            params += `&my_bug_reports=1`;
        }

        const data = await getRequest(`${endpoints.admin_dashboard.support_tickets.get_list}${params}`);

        if (data) {
            setTableData(data || []);
        }

        setLoading(false);
    };



    const [ filters, setFilters ] = useState(defaultFilters);



    const dataFiltered = applyFilter({
        inputData: tableData.data,
        comparator: getComparator(table.order, table.orderBy),
        filters,
    });
    const denseHeight = table.dense ? 52 : 72;
    const canReset = !isEqual(defaultFilters, filters);
    const notFound = (!dataFiltered.length && canReset) || !dataFiltered.length;

    useEffect(() => {
        getUsers();
    }, []);

    const getUsers = async () => {
        const data = await getRequest(endpoints.admin_dashboard.admin_user.user_role.get_list);
        const transformedOptions = data?.map(user => ({
            id: user.id,
            name: `${user.first_name} ${user.last_name}`,
        })) || [];
        console.log("🚀 ~ transformedOptions ~ transformedOptions:", transformedOptions)
        setLoading(false);
        if (data) {
            setAdminUsers(transformedOptions);
        }
    };

    const deleteCallBack = () => {
        confirm.onFalse();
        getSupportTicket();
    };
    const handleDeleteRow = async (id) => {
        setDeleteLoading(true);
        await deleteRequest(`${endpoints.admin_dashboard.support_tickets.delete}/${id}`, deleteCallBack);

        setDeleteLoading(false);
    };

    const handleBulkDelete = async () => {
        const Ids = table.selected;
        console.log("🚀 ~ handleBulkDelete ~ Ids:", Ids);
        if (Ids.length > 0) {
            try {
                setDeleteLoading(true)
                const response = await postRequest(endpoints.accounting.customers.bulk_delete, { customer_ids: Ids });
                setDeleteLoading(false)
                console.log("🚀 ~ handleBulkDelete ~ response:", response);
                // remove.onFalse();
                getSupportTicket();
            } catch (error) {
                console.error('Error during bulk delete:', error);
            }
        } else {
            console.warn('No rows selected');
        }
    };

    const handleEditRow = (item) => {
        setSelectedItem(item);
        // add.onTrue();
    };

    const handleCloseForm = () => {
        // create.onFalse();
        setSelectedItem({});
        table.onSelectAllRows(false, []);
    };
    const handleFilters = useCallback(
        (name, value) => {
            table.onResetPage();
            setFilters((prevState) => ({
                ...prevState,
                [ name ]: value,
            }));
        },
        [ table ]
    );

    const handleFilterStatus = useCallback(
        (event, newValue) => {
            handleFilters('status', newValue);
        },
        [ handleFilters ]
    );

    const handleResetFilters = useCallback(() => {
        setFilters(defaultFilters);
    }, []);

    return (
        <>
            { loading ? (
                <Box height={ 700 }>
                    <LoadingScreen />
                </Box>
            ) : (
                <Container maxWidth={ settings.themeStretch ? false : 'xl' }>

                    <CustomBreadcrumbs
                        heading="Support Tickets"
                        links={ [
                            { name: '', href: paths.dashboard.root },

                        ] }
                        action={
                            <>
                                { canCreate && <Button

                                    onClick={ () => {
                                        // setSelectedItem({});
                                        create.onTrue();
                                    } }
                                    variant="contained"
                                    startIcon={ <Iconify icon="mingcute:add-line" /> }
                                >
                                    Create
                                </Button>
                                }
                            </>
                        }
                        sx={ {
                            mb: { xs: 3, md: 5 },
                        } }
                    />


                    <Card>

                        <UserTableToolbar
                            filters={ filters }
                            onFilters={ handleFilters }
                            //
                            roleOptions={ _roles }
                            handleChange={ handleChange }
                            selectedProject={ selectedProject }

                        />
                        { canReset && (
                            <UserTableFiltersResult
                                filters={ filters }
                                onFilters={ handleFilters }
                                //
                                onResetFilters={ handleResetFilters }
                                //
                                results={ dataFiltered.length }

                                sx={ { p: 2.5, pt: 0 } }
                            />
                        ) }
                        <TableContainer sx={ { position: 'relative', overflow: 'unset' } }>
                            <TableSelectedAction
                                dense={ table.dense }
                                numSelected={ table?.selected.length }
                                rowCount={ tableData?.length }
                                onSelectAllRows={ (checked) =>
                                    table.onSelectAllRows(
                                        checked,
                                        tableData?.map((row) => row.id)
                                    )
                                }
                                action={
                                    <Tooltip title="Delete">
                                        <IconButton color="primary" onClick={ confirm.onTrue }>
                                            <Iconify icon="solar:trash-bin-trash-bold" />
                                        </IconButton>
                                    </Tooltip>
                                }
                            />
                            <Scrollbar>
                                <Table size={ table.dense ? 'small' : 'medium' } sx={ { minWidth: 960 } }>
                                    <TableHeadCustom
                                        order={ table?.order }
                                        orderBy={ table?.orderBy }
                                        headLabel={ TABLE_HEAD }
                                        rowCount={ tableData?.length }
                                        numSelected={ table?.selected.length }
                                        onSort={ table?.onSort }
                                        onSelectAllRows={ (checked) =>
                                            table?.onSelectAllRows(
                                                checked,
                                                tableData?.map((row) => row.id)
                                            )
                                        }
                                    />
                                    <TableBody>
                                        { dataFiltered?.map((row) => (
                                            <UserTableRow
                                                key={ row.id }
                                                row={ row }
                                                selected={ table.selected.includes(row.id) }
                                                onSelectRow={ () => table.onSelectRow(row.id) }
                                                onDeleteRow={ () => handleDeleteRow(row.id) }
                                                onEditRow={ () => handleEditRow(row.id) }
                                                deleteLoading={ deleteLoading }
                                                reload={ () => getSupportTicket() }
                                                adminUsers={ adminUsers }
                                            />
                                        )) }

                                        <TableEmptyRows
                                            height={ denseHeight }
                                            emptyRows={ emptyRows(table?.page, table?.rowsPerPage, tableData?.length) }
                                        />

                                        {/* <TableNoData notFound={ notFound } /> */ }
                                    </TableBody>
                                </Table>
                            </Scrollbar>
                        </TableContainer>
                        <TablePaginationApiBased

                            paginationLinks={ tableData } // The pagination links from the backend response
                            onPageChange={ (page) => getSupportTicket(page) } // A function that fetches data based on the page number
                        />
                    </Card>
                </Container>
            ) }
            <SupportTicketCreateModal
                open={ create.value }
                onClose={ create.onFalse }
                reload={ () => getSupportTicket() }
            />
            <ConfirmDialog
                open={ confirm.value }
                onClose={ confirm.onFalse }
                title="Delete"
                content={
                    <>
                        Are you sure want to delete <strong> { table.selected.length } </strong> items?
                    </>
                }
                action={
                    <Button
                        variant="contained"
                        color="error"
                        onClick={ () => {
                            // handleDeleteRows();
                            confirm.onFalse();
                        } }
                    >
                        Delete
                    </Button>
                }
            />
        </>
    );
}

// ----------------------------------------------------------------------

